import type { LotusLobbyGroupConfig } from '../constants/lotusCasinoLobbyGroups'
import { LOTUS_TOP_PROVIDERS } from '../constants/lotusCasinoTopProviders'

export type CasinoGameRecord = Record<string, unknown>

export function getGameId(game: CasinoGameRecord): string {
  return String(game.identifier ?? game.game_id ?? game._id ?? '')
}

/** Text used for regex category matching — excludes provider to avoid cross-tab pollution. */
export function getGameMatchText(game: CasinoGameRecord): string {
  const parts = [
    game.game_name,
    game.name,
    game.game_category,
    game.game_group,
    game.live_category,
    game.slot_category,
    game.game_type,
    game.table_name,
    game.game_sub_type,
  ]
  return parts
    .filter(Boolean)
    .map((p) => String(p).toLowerCase())
    .join(' ')
}

export function getGameSearchText(game: CasinoGameRecord): string {
  const parts = [
    game.game_name,
    game.name,
    game.game_category,
    game.game_group,
    game.live_category,
    game.slot_category,
    game.provider,
    game.provider_name,
    game.game_provider,
    game.game_type,
    game.table_name,
    game.game_sub_type,
  ]
  return parts
    .filter(Boolean)
    .map((p) => String(p).toLowerCase())
    .join(' ')
}

export function matchesPatterns(game: CasinoGameRecord, patterns?: RegExp[]): boolean {
  if (!patterns?.length) return true
  const text = getGameMatchText(game)
  return patterns.some((re) => re.test(text))
}

export function matchesExcludePatterns(game: CasinoGameRecord, patterns?: RegExp[]): boolean {
  if (!patterns?.length) return false
  const text = getGameMatchText(game)
  return patterns.some((re) => re.test(text))
}

function matchesProvider(
  game: CasinoGameRecord,
  providerFilter: Set<string> | null,
): boolean {
  if (!providerFilter) return true
  const prov = String(
    game._providerCategory ?? game.provider ?? game.provider_name ?? game.game_provider ?? '',
  ).toLowerCase()
  return providerFilter.has(prov)
}

function matchesCategoryLabel(game: CasinoGameRecord, group: LotusLobbyGroupConfig): boolean {
  const fields = [game.game_category, game.live_category, game.slot_category, game.game_group]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())

  if (!fields.length) return false

  const aliases: Record<string, string[]> = {
    'teen-patti': ['teen patti', 'teenpatti', '3 patti', 'indian'],
    roulette: ['roulette'],
    baccarat: ['baccarat'],
    blackjack: ['blackjack', 'black jack'],
    poker: ['poker', 'holdem', "hold'em", 'texas holdem'],
    'dragon-tiger': ['dragon tiger', 'dragontiger', 'dragon & tiger'],
    sicbo: ['sicbo', 'sic bo', 'super dice', 'tai sai', 'fan tan'],
    'game-shows': ['game show', 'game shows', 'game show live'],
    'instant-games': ['instant', 'crash', 'instant game'],
    slots: ['slot', 'slots'],
    'sport-games': ['sport', 'sports', 'virtual sport', 'virtual sports'],
    evolution: ['live', 'game show', 'roulette', 'blackjack', 'baccarat'],
  }

  const keys = aliases[group.id] ?? [group.title.toLowerCase()]
  return fields.some((field) =>
    keys.some((key) => field === key || field.includes(key) || key.includes(field)),
  )
}

function gameMatchesGroup(
  game: CasinoGameRecord,
  group: LotusLobbyGroupConfig,
  providerFilter: Set<string> | null,
): boolean {
  if (!matchesProvider(game, providerFilter)) return false
  if (group.excludePatterns?.length && matchesExcludePatterns(game, group.excludePatterns)) {
    return false
  }
  if (!group.patterns?.length) return true
  if (matchesCategoryLabel(game, group)) return true
  return matchesPatterns(game, group.patterns)
}

export function filterGamesForLobbyGroup(
  pool: CasinoGameRecord[],
  group: LotusLobbyGroupConfig,
  seen: Set<string>,
): CasinoGameRecord[] {
  const result: CasinoGameRecord[] = []

  if (group.useTopProviders) {
    const byProvider = new Map<string, CasinoGameRecord[]>()
    for (const game of pool) {
      const prov = String(game._providerCategory ?? game.provider ?? '')
      if (!byProvider.has(prov)) byProvider.set(prov, [])
      byProvider.get(prov)!.push(game)
    }
    let round = 0
    while (result.length < group.maxGames) {
      let added = false
      for (const prov of LOTUS_TOP_PROVIDERS) {
        const list = byProvider.get(prov.categoryId) ?? []
        const game = list[round]
        if (!game) continue
        const id = getGameId(game)
        if (!id || seen.has(id)) continue
        seen.add(id)
        result.push(game)
        added = true
        if (result.length >= group.maxGames) break
      }
      if (!added) break
      round += 1
    }
    return result
  }

  const providerFilter = group.providerCategories?.length
    ? new Set(group.providerCategories.map((p) => p.toLowerCase()))
    : null

  for (const game of pool) {
    if (result.length >= group.maxGames) break
    if (!gameMatchesGroup(game, group, providerFilter)) continue
    const id = getGameId(game)
    if (!id || seen.has(id)) continue
    seen.add(id)
    result.push(game)
  }

  return result
}
